import { type AuthUser } from "wasp/auth";
import {
  getAdminBrokerHealth,
  getAdminTradovateQuota,
  getAdminUsersByPlan,
  getDailyStats,
  useQuery,
} from "wasp/client/operations";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { cn } from "../../../client/utils";
import DefaultLayout from "../../layout/DefaultLayout";
import RevenueAndProfitChart from "./RevenueAndProfitChart";
import TotalPageViewsCard from "./TotalPageViewsCard";
import TotalPayingUsersCard from "./TotalPayingUsersCard";
import TotalRevenueCard from "./TotalRevenueCard";
import TotalSignupsCard from "./TotalSignupsCard";
import MrrOverviewCards from "./MrrOverviewCards";
import UsersByPlanColumns from "./UsersByPlanColumns";
import RetentionCohortCard from "./RetentionCohortCard";
import AcquisitionCard from "./AcquisitionCard";
import BrokerHealthCard from "./BrokerHealthCard";
import TradovateQuotaCard from "./TradovateQuotaCard";
import TradovateQuotaHistoryCard from "./TradovateQuotaHistoryCard";
import TradovateEndpointBreakdownCard from "./TradovateEndpointBreakdownCard";
import TradovateErrorsCard from "./TradovateErrorsCard";
import TradovateBetaDiagnosticCard from "./TradovateBetaDiagnosticCard";
import TradovateReauthCard from "./TradovateReauthCard";
import TradovateBackfillRepairCard from "./TradovateBackfillRepairCard";
import UserDetailSheet from "./UserDetailSheet";

const Dashboard = ({ user }: { user: AuthUser }) => {
  const navigate = useNavigate();
  const { data: stats, isLoading } = useQuery(getDailyStats);
  const {
    data: usersByPlan,
    isLoading: isLoadingUsers,
    error: usersError,
  } = useQuery(getAdminUsersByPlan);
  const { data: brokerHealth } = useQuery(getAdminBrokerHealth);
  const { data: tradovateQuota } = useQuery(getAdminTradovateQuota);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  useEffect(() => {
    if (!user.isAdmin) {
      navigate("/dashboard");
    }
  }, [user.isAdmin, navigate]);

  // Seule une erreur sur la liste des utilisateurs est bloquante : les stats
  // journalieres dependent d'un job qui peut n'avoir jamais tourne, et leur
  // absence ne doit pas masquer le reste de la page.
  if (usersError) {
    return (
      <DefaultLayout user={user}>
        <div className="flex h-full items-center justify-center">
          <div className="bg-card rounded-lg p-8 shadow-lg">
            <p className="text-2xl font-bold text-red-500">Error</p>
            <p className="text-muted-foreground mt-2 text-sm">
              {usersError.message ||
                "Something went wrong while fetching users."}
            </p>
          </div>
        </div>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout user={user}>
      <div className="flex flex-col gap-6">
        <div className="relative">
          <div className={cn({ "opacity-25": !stats })}>
            <div className="2xl:gap-7.5 grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6 xl:grid-cols-4">
              <TotalPageViewsCard
                totalPageViews={stats?.dailyStats.totalViews}
                prevDayViewsChangePercent={
                  stats?.dailyStats.prevDayViewsChangePercent
                }
              />
              <TotalRevenueCard
                dailyStats={stats?.dailyStats}
                weeklyStats={stats?.weeklyStats}
                isLoading={isLoading}
              />
              <TotalPayingUsersCard
                dailyStats={stats?.dailyStats}
                isLoading={isLoading}
              />
              <TotalSignupsCard
                dailyStats={stats?.dailyStats}
                isLoading={isLoading}
              />
            </div>

            <div className="2xl:mt-7.5 2xl:gap-7.5 mt-4 grid grid-cols-12 gap-4 md:mt-6 md:gap-6">
              <RevenueAndProfitChart
                weeklyStats={stats?.weeklyStats}
                isLoading={isLoading}
              />
            </div>
          </div>

          {!stats && !isLoading && (
            <div className="bg-background/50 absolute inset-0 flex items-start justify-center">
              <div className="bg-card rounded-lg p-8 shadow-lg">
                <p className="text-foreground text-2xl font-bold">
                  No daily stats generated yet
                </p>
                <p className="text-muted-foreground mt-2 text-sm">
                  Stats will appear here once the daily stats job has run
                </p>
              </div>
            </div>
          )}
        </div>

        {usersByPlan && (
          <>
            <MrrOverviewCards
              mrr={usersByPlan.mrr}
              mrrMax={usersByPlan.mrrMax}
              mrrPro={usersByPlan.mrrPro}
              totalUsers={usersByPlan.totalUsers}
              paymentIssueCount={usersByPlan.paymentIssueCount}
            />
            <UsersByPlanColumns
              columns={usersByPlan.columns}
              activeLast7Days={usersByPlan.activeLast7Days}
              isLoading={isLoadingUsers}
              onSelectUser={setSelectedUserId}
            />
            <RetentionCohortCard cohorts={usersByPlan.cohorts} />
            <AcquisitionCard
              signupsLast7Days={usersByPlan.signupsLast7Days}
              signupsLast30Days={usersByPlan.signupsLast30Days}
              sources={usersByPlan.sources}
            />
          </>
        )}

        {brokerHealth && (
          <BrokerHealthCard
            rows={brokerHealth.rows}
            totalConnections={brokerHealth.totalConnections}
            onSelectUser={setSelectedUserId}
          />
        )}

        {tradovateQuota && (
          <TradovateQuotaCard data={tradovateQuota} onSelectUser={setSelectedUserId} />
        )}

        {/* La carte ci-dessus répond à « où en est-on maintenant », celle-ci à
            « comment on en est arrivé là ». Montée inconditionnellement : elle
            porte son propre état vide, et la masquer priverait de l'explication
            au moment précis où l'historique démarre. */}
        <TradovateQuotaHistoryCard />

        <TradovateEndpointBreakdownCard />

        {/* QUAND et POURQUOI le quota est tapé (B1) : incidents 429/423
            horodatés, p-time, p-captcha, ventilation par endpoint. Montée
            inconditionnellement : son état vide (« aucune pénalité ») est
            précisément le constat qu'on vient chercher. */}
        <TradovateErrorsCard />

        <TradovateBetaDiagnosticCard />

        <TradovateReauthCard />

        <TradovateBackfillRepairCard />

        <UserDetailSheet
          userId={selectedUserId}
          onOpenChange={(open) => {
            if (!open) setSelectedUserId(null);
          }}
        />
      </div>
    </DefaultLayout>
  );
};

export default Dashboard;
